/**
 * Klaviyo Back in Stock — drawer trigger + in-drawer subscription form.
 *
 * Opens a dev-drawer on CTA click; submits to Klaviyo Client API from the drawer footer.
 * Syncs variant summary on Horizon variant:update (image, color label, submit variant ID).
 */
class DevBackInStock extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number} */
  #colorOptionPosition = 0;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const position = Number.parseInt(this.dataset.colorOptionPosition || '0', 10);
    if (position > 0) {
      this.#colorOptionPosition = position;
    }

    this.querySelectorAll('[data-ref="open-drawer"]').forEach((trigger) => {
      trigger.addEventListener('click', this.#openDrawer, { signal });
    });

    this.#getDrawer()?.addEventListener('dialog:open', this.#onDrawerOpen, { signal });

    const submitButton = this.querySelector('[data-ref="submit-button"]');
    const emailInput = this.querySelector('[data-ref="email-input"]');

    submitButton?.addEventListener('click', this.#onSubmit, { signal });
    emailInput?.addEventListener('keydown', this.#onEmailKeydown, { signal });

    this.closest('.shopify-section')?.addEventListener('variant:update', this.#onVariantUpdate, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @param {KeyboardEvent} event */
  #onEmailKeydown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.#onSubmit();
  };

  #openDrawer = () => {
    const drawer = this.#getDrawer();
    if (drawer && typeof drawer.showDialog === 'function') {
      drawer.showDialog();
    }
  };

  #onDrawerOpen = () => {
    const emailInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-ref="email-input"]'));
    if (!emailInput) return;

    const dialog = this.#getDrawer()?.querySelector('dialog');
    const focusEmail = () => emailInput.focus({ preventScroll: true });

    if (dialog instanceof HTMLDialogElement && dialog.hasAttribute('data-slide-right')) {
      requestAnimationFrame(() => {
        requestAnimationFrame(focusEmail);
      });
      return;
    }

    requestAnimationFrame(focusEmail);
  };

  #getDrawer() {
    const drawerId = this.dataset.drawerId;
    if (!drawerId) return null;

    const drawer = document.getElementById(drawerId);
    if (drawer && 'showDialog' in drawer) {
      return /** @type {HTMLElement & { showDialog: () => void }} */ (drawer);
    }

    return null;
  }

  /** @param {CustomEvent} event */
  #onVariantUpdate = (event) => {
    const resource = event.detail?.resource;
    if (!resource?.id) return;

    this.#setActiveVariant(String(resource.id), this.#optionValueFromVariant(resource));
    this.#updateProductImage(resource);
  };

  /**
   * @param {string} variantId
   * @param {string} [optionValue]
   */
  #setActiveVariant(variantId, optionValue) {
    const submitButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-ref="submit-button"]'));
    if (submitButton) {
      submitButton.dataset.variantId = variantId;
    }

    if (optionValue) {
      const variantValueEl = this.querySelector('[data-ref="variant-value"]');
      const variantLineEl = this.querySelector('[data-ref="variant-line"]');
      if (variantValueEl) {
        variantValueEl.textContent = optionValue;
      }
      variantLineEl?.removeAttribute('hidden');
    }
  }

  /**
   * @param {object} resource
   */
  #updateProductImage(resource) {
    const featuredMedia = resource.featured_media;
    const preview = featuredMedia?.preview_image;
    const imageUrl = preview?.src || preview?.url;
    if (!imageUrl) return;

    const img = this.querySelector('[data-back-in-stock-media] img');
    if (img instanceof HTMLImageElement) {
      img.src = imageUrl;
    }
  }

  /**
   * @param {object} variant
   * @returns {string | undefined}
   */
  #optionValueFromVariant(variant) {
    if (this.#colorOptionPosition === 1) return variant.option1;
    if (this.#colorOptionPosition === 2) return variant.option2;
    if (this.#colorOptionPosition === 3) return variant.option3;
    return undefined;
  }

  #onSubmit = async () => {
    const emailInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-ref="email-input"]'));
    const submitButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-ref="submit-button"]'));
    const messageElement = this.querySelector('[data-ref="notification-message"]');
    const consentId = this.dataset.consentDescribedby;
    const consentInput = consentId
      ? /** @type {HTMLInputElement | null} */ (this.querySelector(`#${CSS.escape(consentId)}`))
      : null;

    if (!emailInput || !submitButton || !messageElement) return;

    const email = emailInput.value.trim();
    const companyId = this.dataset.klaviyoCompanyId;

    if (!companyId) {
      this.#showMessage(emailInput, messageElement, this.dataset.msgError || 'There was an error. Please try again.', 'error');
      return;
    }

    if (!this.#validateEmail(email)) {
      emailInput.setAttribute('aria-invalid', 'true');
      this.#showMessage(
        emailInput,
        messageElement,
        this.dataset.msgInvalidEmail || 'Please enter a valid email address.',
        'error'
      );
      return;
    }

    if (consentInput && !consentInput.checked) {
      consentInput.setAttribute('aria-invalid', 'true');
      this.#showMessage(
        emailInput,
        messageElement,
        this.dataset.msgConsentRequired || 'Please accept the privacy policy to continue.',
        'error'
      );
      return;
    }

    emailInput.setAttribute('aria-invalid', 'false');
    consentInput?.setAttribute('aria-invalid', 'false');
    this.#setDescribedBy(emailInput, messageElement, 'default');

    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant') || submitButton.dataset.variantId;

    if (!variantId) {
      this.#showMessage(emailInput, messageElement, this.dataset.msgError || 'There was an error. Please try again.', 'error');
      return;
    }

    submitButton.disabled = true;

    try {
      const response = await this.#subscribeToBackInStock(email, variantId, companyId);

      if (response.ok) {
        this.#showMessage(
          emailInput,
          messageElement,
          this.dataset.msgSuccess ||
            'Thank you! You will be notified when the product is back in stock.',
          'success'
        );

        if (typeof window._backInStockForm === 'function') {
          window._backInStockForm();
        }
      } else {
        let errorDetail = this.dataset.msgError || 'There was an error. Please try again.';

        try {
          const responseData = await response.json();
          const apiDetail = responseData.errors?.[0]?.detail;
          const errorTemplate = this.dataset.msgErrorDetail;

          if (apiDetail && errorTemplate) {
            errorDetail = errorTemplate.replace('{{ detail }}', apiDetail);
          } else if (apiDetail) {
            errorDetail = apiDetail;
          }
        } catch {
          // Keep generic error message when response body is not JSON.
        }

        this.#showMessage(emailInput, messageElement, errorDetail, 'error');
        submitButton.disabled = false;
      }
    } catch (error) {
      console.error('Back in stock subscription failed:', error);
      this.#showMessage(emailInput, messageElement, this.dataset.msgError || 'There was an error. Please try again.', 'error');
      submitButton.disabled = false;
    }
  };

  /**
   * @param {string} email
   * @returns {boolean}
   */
  #validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.toLowerCase());
  }

  /**
   * @param {HTMLInputElement} emailInput
   * @param {HTMLElement} messageElement
   * @param {'default' | 'error' | 'success'} mode
   */
  #setDescribedBy(emailInput, messageElement, mode) {
    const consentId = this.dataset.consentDescribedby;
    const messageId = messageElement.id;

    if (mode === 'error' && consentId && messageId) {
      emailInput.setAttribute('aria-describedby', `${consentId} ${messageId}`);
      return;
    }

    if (consentId) {
      emailInput.setAttribute('aria-describedby', consentId);
    }
  }

  /**
   * @param {HTMLInputElement} emailInput
   * @param {HTMLElement} messageElement
   * @param {string} text
   * @param {'success' | 'error'} type
   */
  #showMessage(emailInput, messageElement, text, type) {
    messageElement.textContent = text;
    messageElement.hidden = false;
    messageElement.classList.remove('hidden', 'dev-back-in-stock-drawer__message--success', 'dev-back-in-stock-drawer__message--error');
    messageElement.classList.add(
      type === 'success' ? 'dev-back-in-stock-drawer__message--success' : 'dev-back-in-stock-drawer__message--error'
    );

    this.#setDescribedBy(emailInput, messageElement, type === 'error' ? 'error' : 'success');
    this.#retranslateMessage(messageElement);

    requestAnimationFrame(() => {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  /**
   * Shopify `| t` strings land in EN under Weglot; re-scan after JS injects success/error copy.
   * @param {HTMLElement} messageElement
   */
  #retranslateMessage(messageElement) {
    if (window.NoomaWeglotChrome?.retranslate) {
      window.NoomaWeglotChrome.retranslate(messageElement);
      return;
    }

    window.addEventListener(
      'weglot:ready',
      () => {
        window.NoomaWeglotChrome?.retranslate?.(messageElement);
      },
      { once: true }
    );
  }

  /**
   * @param {string} email
   * @param {string} variantId
   * @param {string} companyId
   * @returns {Promise<Response>}
   */
  async #subscribeToBackInStock(email, variantId, companyId) {
    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    };

    return fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2024-06-15',
      },
      body: JSON.stringify(payload),
    });
  }
}

if (!customElements.get('dev-back-in-stock')) {
  customElements.define('dev-back-in-stock', DevBackInStock);
}
